// Opening-focus handlers for `AppDialog`'s `onOpenAutoFocus`, kept apart from
// the component file so it exports components only.

/** `enteringScreen` — when MUI's `onEntered` fires; `AppDialog`'s `SHELL_CLOCK`. */
const ENTER_MS = 225;
/** `focusFirstInput`'s own wait after `onEntered` (`utils/focusUtils.ts`). */
const FOCUS_FIRST_INPUT_DELAY_MS = 50;

/** MUI's trap focuses the paper when nothing inside asks for the focus. */
export function focusPaper(event: Event) {
  event.preventDefault();
  (event.currentTarget as HTMLElement | null)?.focus();
}

/**
 * An `onOpenAutoFocus` for a dialog whose donor opens on its paper and then
 * focuses its first field once it has entered — the donor's
 * `slotProps.transition.onEntered: focusFirstInput`, where nothing inside
 * holds focus at mount, so MUI's trap focuses the paper (the spec dialogs'
 * `autoFocus` on an `InputField` lands on its `FormControl` div and does
 * nothing). The paper takes the focus at once, and the first input 50ms after
 * the 225ms entrance — or, in a dialog with no input, its first select.
 *
 * **Each entrance has its own clock**, as `onEntered` is per transition: a
 * close cancels the pending focus (a dialog closed mid-entrance never
 * enters), and a reopen during the exit — which keeps Radix's paper mounted,
 * so this handler is not called again — starts the clock over.
 */
export function focusPaperThenFirstInput(event: Event) {
  focusPaperThen(event, (paper) =>
    // The donor's own order (`focusFirstInput`, `utils/focusUtils.ts:12-19`):
    // a text input first, and only a dialog with none falls back to its
    // select — MUI's `.MuiSelect-select`, here a `combobox` trigger. The
    // donor then `.click()`s it, which does not open MUI 9's select (it opens
    // on `mousedown`, `SelectInput.js:730`), so this focuses and nothing more.
    (
      paper.querySelector<HTMLElement>("input:not([type=hidden]), textarea") ??
      paper.querySelector<HTMLElement>("[role=combobox]")
    )?.focus(),
  );
}

/**
 * The same entrance for a dialog with **no field at all** — Create Version's
 * (#812). `focusFirstInput`'s last selector, `[tabindex]:not([tabindex="-1"])`
 * (`utils/focusUtils.ts:17`), matches the first MUI `ButtonBase` in the paper,
 * which writes `tabindex="0"` on itself: the titlebar's X. Here that is the
 * paper's first button.
 */
export function focusPaperThenFirstButton(event: Event) {
  focusPaperThen(event, (paper) =>
    paper.querySelector<HTMLElement>("button:not([disabled])")?.focus(),
  );
}

function focusPaperThen(event: Event, focusInside: (paper: HTMLElement) => void) {
  focusPaper(event);
  const paper = event.currentTarget as HTMLElement | null;
  if (!paper) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const enter = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      // Unconditional once there, as the donor's is: a field clicked in the
      // meantime loses the focus to the first one.
      if (!paper.isConnected) return;
      focusInside(paper);
    }, ENTER_MS + FOCUS_FIRST_INPUT_DELAY_MS);
  };
  enter();
  const states = new MutationObserver(() => {
    if (!paper.isConnected) {
      states.disconnect();
      clearTimeout(timer);
    } else if (paper.dataset.state === "open") enter();
    else clearTimeout(timer);
  });
  states.observe(paper, { attributes: true, attributeFilter: ["data-state"] });
}
